import React, { useEffect, useState } from "react";
import { Table, Tab, Tabs, Pagination } from "react-bootstrap";
import Main from "../Components/Main";
import PageContainer from "../Components/PageContainer";
import CollapseCard from "../Components/CollapseCard";
import { getAllBillsPaginated, saveBill } from "../Service/BillService";
import { useToast } from "../Components/toast/ToastProvider";
import { getAllVehicles } from "../Service/VehicleService";
import { formatDate } from "../config/function";
import { getAllVendors } from "../Service/VendorService";
import "../Style/toggle.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "react-feather";
import { getBillById } from "../Service/BillService";
import { getSettings } from "../Service/AppSettingsService";
import {
  isThermalPrinterFromSettings,
  postThermalBillReceiptIfNeeded,
} from "../utils/thermalBillReceiptPrint";

const PAGE_SIZE = 10;

const BillingViewPage = () => {
  const [trips, setTrips] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const toast = useToast();
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState("new");
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchVehicles();
    fetchVendors();
  }, []);

  useEffect(() => {
    fetchBillTrips(currentPage, PAGE_SIZE, activeTab);
  }, [activeTab, currentPage]);

  const fetchBillTrips = async (page, limit, status) => {
    try {
      const res = await getAllBillsPaginated(page, limit, status);

      const billsArray = Array.isArray(res.data.data) ? res.data.data : [];
      const total = res.data.pagination?.total_records || 0;
      const pages = res.data.pagination?.total_pages || 0;

      setTrips(billsArray);
      setTotalRecords(total);
      setTotalPages(pages);
    } catch (err) {
      toast(err.message || "Failed to fetch bills", "ERROR");
    }
  };

  const fetchVehicles = async () => {
    try {
      const res = await getAllVehicles();
      setVehicles(res.data || []);
    } catch (err) {
      toast(err.message || "Failed to fetch vehicles", "ERROR");
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await getAllVendors();
      setVendors(res.data || []);
    } catch (err) {
      toast(err.message || "Failed to fetch vendors", "ERROR");
    }
  };

  const updateBillStatus = async (bill, nextStatus) => {
    const res = await getBillById(bill.id);
    const trips = res?.data?.trips || [];
    const payload = { ...bill, status: nextStatus, trips };
    await saveBill(payload);
  };

  const handleToggleStatus = async (bill) => {
    const nextStatus = bill.status === "process" ? "paid" : "process";
    try {
      setUpdatingId(bill.id);
      await updateBillStatus(bill, nextStatus);
      toast(`Bill moved to ${nextStatus.toUpperCase()}`, "SUCCESS");
      fetchBillTrips(currentPage, PAGE_SIZE, activeTab);
    } catch (err) {
      toast(err.message || "Failed to update bill status", "ERROR");
    } finally {
      setUpdatingId(null);
    }
  };

  const renderStatusBadge = (status) => {
    const map = { new: "secondary", process: "warning", paid: "success" };
    return <span className={`badge bg-${map[status]}`}>{status}</span>;
  };

  const renderPaginationItems = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);

    if (start > 1) {
      pages.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>,
      );
      if (start > 2) {
        pages.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={currentPage === i}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      pages.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    return pages;
  };

  const addPDFHeader = (doc, pageWidth) => {
    const logo = new Image();
    logo.src = "/img/asa_logo.png";
    const logoWidth = 15,
      logoHeight = 15,
      spacing = 2;
    const headerText = "ASA Transport";
    const subHeaderText = "Mannargudi,Thiruvarur Dt.";
    const headerWidth = doc.getTextWidth(headerText);
    const subHeaderWidth = doc.getTextWidth(subHeaderText);
    const totalWidth =
      logoWidth + spacing + Math.max(headerWidth, subHeaderWidth);
    const startX = (pageWidth - totalWidth) / 2 + 10;
    try {
      doc.addImage(logo, "PNG", startX, 8, logoWidth, logoHeight);
    } catch (e) {
      console.warn("Logo load skipped");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(headerText, startX + logoWidth + spacing, 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subHeaderText, startX + logoWidth + spacing, 20);
  };

  const printBill = async (bill) => {
    try {
      const res = await getBillById(bill.id);
      const billData = res?.data?.bill || res?.data;
      const tripsData = res?.data?.trips || [];
      if (!billData) {
        toast("Bill data not found", "ERROR");
        return;
      }

      const vendor = vendors.find((ve) => ve.id === billData.vendor_id);
      const enrichedTrips = tripsData.map((t) => {
        const vehicle = vehicles.find((v) => v.id === t.vehicle_id);
        return {
          ...t,
          date_formatted: formatDate(t.date),
          vehicle_number: vehicle?.vehicle_number,
        };
      });

      const settings = await getSettings();
      if (isThermalPrinterFromSettings(settings)) {
        const result = await postThermalBillReceiptIfNeeded({
          bill: billData,
          trips: enrichedTrips,
          vendorName: vendor?.name,
          settings,
          billDateFormatted: formatDate(billData.date),
        });
        if (result.attempted && !result.ok) {
          toast(result.error || "Thermal print failed", "ERROR");
        } else if (result.attempted) {
          toast("Sent to thermal printer", "SUCCESS");
        }
        return;
      }

      generatePDF(billData, enrichedTrips, vendor);
    } catch (err) {
      toast(err.message || "Failed to print bill", "ERROR");
    }
  };

  const generatePDF = (bill, trips = [], vendorFromCaller) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    addPDFHeader(doc, pageWidth);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("BILL RECEIPT", pageWidth / 2, 30, { align: "center" });
    const capitalize = (str) => {
      if (!str) return "N/A";
      return str
        .toLowerCase()
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };
    const vendorName =
      vendorFromCaller || vendors.find((ve) => ve.id === bill.vendor_id);
    let y = 40;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Vendor Name: ${capitalize(vendorName?.name || "N/A")}`, 14, y);
    doc.text(`Bill No: ${bill.id}`, pageWidth - 50, y);
    doc.text(`Date: ${formatDate(bill.date)}`, pageWidth - 50, y + 7);
    const tableRows = trips.map((t, i) => {
      const vehicleNum =
        t.vehicle_number ||
        vehicles.find((v) => v.id === t.vehicle_id)?.vehicle_number ||
        "N/A";
      return [
        i + 1,
        `${t.date_formatted || formatDate(t.date)}- ${vehicleNum}`,
        `${t.source_name} - ${t.destination_name}`,
        t.total_km,
        Number(t.rate).toFixed(2),
        t.total_tones,
        Number(t.amount).toFixed(2),
        Number(t.transition_charge).toFixed(2),
        Number(t.advance_amount || 0).toFixed(2),
        Number(t.balance_amount || 0).toFixed(2),
      ];
    });
    autoTable(doc, {
      startY: y + 10,
      head: [
        [
          "S.No",
          "Date",
          "Trip",
          "KM",
          "Rate",
          "Tones",
          "Amount",
          "Weighment",
          "Advance Amount",
          "Balance Amount",
        ],
      ],
      body: tableRows,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        fontStyle: "bold",
      },
    });
    let finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont("helvetica", "normal");
    doc.text(
      `Normal Deduction : ${Number(bill.nor_advance_deduction || 0).toFixed(2)}`,
      14,
      finalY,
    );
    finalY += 7;
    doc.text(
      `Receipt Deduction : ${Number(bill.res_advance_deduction || 0).toFixed(2)}`,
      14,
      finalY,
    );
    let rightY = doc.lastAutoTable.finalY + 10;
    doc.text(
      `Total Amount : ${Number(bill.total_amount || 0).toFixed(2)}`,
      pageWidth - 70,
      rightY,
    );
    rightY += 7;
    doc.setFont("helvetica", "bold");
    doc.text(
      `Payable Amount : ${Number(bill.payable_amount || 0).toFixed(2)}`,
      pageWidth - 70,
      rightY,
    );
    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  };

  return (
    <Main>
      <PageContainer>
        <CollapseCard title="Billing View">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              setCurrentPage(1);
            }}
            className="mb-3"
            style={{ cursor: "pointer" }}
          >
            <Tab
              eventKey="new"
              title={
                <span style={{ color: activeTab === "new" ? "blue" : "black" }}>
                  New
                </span>
              }
            />
            <Tab
              eventKey="process"
              title={
                <span
                  style={{ color: activeTab === "process" ? "blue" : "black" }}
                >
                  Process
                </span>
              }
            />
            <Tab
              eventKey="paid"
              title={
                <span
                  style={{ color: activeTab === "paid" ? "blue" : "black" }}
                >
                  Paid
                </span>
              }
            />
          </Tabs>

          <Table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>
                <th>Vendor Name</th>
                <th>Trip Count</th>
                <th>Total Amount</th>
                {activeTab === "new" && <th>Status</th>}
                {(activeTab === "process" || activeTab === "paid") && (
                  <>
                    <th>Normal Deduction</th>
                    <th>Receipt Deduction</th>
                    <th>Payable Amount</th>
                    <th>Paid Status</th>
                  </>
                )}
                {activeTab === "process" && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {trips.map((t, i) => {
                const vendorName = vendors.find((ve) => ve.id === t.vendor_id);
                return (
                  <tr key={t.id}>
                    <td>{(currentPage - 1) * PAGE_SIZE + i + 1}</td>
                    <td>{formatDate(t.date)}</td>
                    <td>{vendorName?.name}</td>
                    <td>{t.trip_count}</td>
                    <td>{t.total_amount?.toFixed(2)}</td>
                    {(activeTab === "process" || activeTab === "paid") && (
                      <>
                        <td>{t.nor_advance_deduction?.toFixed(2) || "0.00"}</td>
                        <td>{t.res_advance_deduction?.toFixed(2) || "0.00"}</td>
                        <td>{t.payable_amount?.toFixed(2) || "0.00"}</td>
                      </>
                    )}
                    <td>
                      {activeTab === "process" || activeTab === "paid" ? (
                        <label
                          className={
                            activeTab === "process"
                              ? "toggle-switch1 toggle-switch1-process"
                              : "toggle-switch1 toggle-switch1-paid"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={activeTab === "process"}
                            disabled={updatingId === t.id}
                            onChange={async () => {
                              setUpdatingId(t.id);
                              await handleToggleStatus(t);
                              setUpdatingId(null);
                            }}
                          />
                          <span className="slider1"></span>
                        </label>
                      ) : (
                        renderStatusBadge(t.status)
                      )}
                    </td>
                    {activeTab === "process" && (
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => printBill(t)}
                        >
                          <Download size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Showing{" "}
              {totalRecords === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} to{" "}
              {Math.min(currentPage * PAGE_SIZE, totalRecords)} of{" "}
              {totalRecords} records
            </small>
            <Pagination className="mb-0">
              <Pagination.First
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              />
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              {renderPaginationItems()}
              <Pagination.Next
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
              <Pagination.Last
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(totalPages)}
              />
            </Pagination>
          </div>
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default BillingViewPage;
